import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import './ShareModal.css'

interface ShareModalProps {
  url: string
  shopName: string
  shopId: string
  onClose: () => void
}

// Position of the dark QR placeholder inside QRimage.png (as fractions of image size)
// Dark box: x=12%→88%, y=30%→68% (stops above the features row)
const QR_X_FRAC = 0.12
const QR_Y_FRAC = 0.30
const QR_W_FRAC = 0.76
const QR_H_FRAC = 0.38

export function ShareModal({ url, shopName, shopId, onClose }: ShareModalProps) {
  const [copied, setCopied] = useState(false)
  const [tab, setTab] = useState<'link' | 'qr'>('link')
  const [compositeUrl, setCompositeUrl] = useState<string>('')
  const [compositeReady, setCompositeReady] = useState(false)
  const previewRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (tab !== 'qr') return
    let cancelled = false

    async function build() {
      setCompositeReady(false)

      const [bgImg, qrDataUrl] = await Promise.all([
        loadImage('/QRimage.png'),
        QRCode.toDataURL(url, {
          width: 600,
          margin: 1,
          color: { dark: '#000000', light: '#ffffff' },
          errorCorrectionLevel: 'H',
        }),
      ])
      const qrImg = await loadImage(qrDataUrl)

      if (cancelled) return

      const W = bgImg.naturalWidth
      const H = bgImg.naturalHeight

      const canvas = document.createElement('canvas')
      canvas.width = W
      canvas.height = H
      const ctx = canvas.getContext('2d')!

      // Draw background
      ctx.drawImage(bgImg, 0, 0, W, H)

      // Calculate QR placement — keep it square, center vertically in the box region
      const qx = Math.round(W * QR_X_FRAC)
      const qy = Math.round(H * QR_Y_FRAC)
      const qw = Math.round(W * QR_W_FRAC)
      const qh = Math.round(H * QR_H_FRAC)
      // Use the smaller dimension so the QR stays square and fits inside
      const size = Math.min(qw, qh)
      const cx = qx + Math.round((qw - size) / 2)
      const cy = qy + Math.round((qh - size) / 2)

      // White rounded-rect background for QR
      const radius = 16
      ctx.beginPath()
      ctx.moveTo(cx + radius, cy)
      ctx.lineTo(cx + size - radius, cy)
      ctx.quadraticCurveTo(cx + size, cy, cx + size, cy + radius)
      ctx.lineTo(cx + size, cy + size - radius)
      ctx.quadraticCurveTo(cx + size, cy + size, cx + size - radius, cy + size)
      ctx.lineTo(cx + radius, cy + size)
      ctx.quadraticCurveTo(cx, cy + size, cx, cy + size - radius)
      ctx.lineTo(cx, cy + radius)
      ctx.quadraticCurveTo(cx, cy, cx + radius, cy)
      ctx.closePath()
      ctx.fillStyle = '#ffffff'
      ctx.fill()

      // Draw QR with small padding
      const pad = 14
      ctx.drawImage(qrImg, cx + pad, cy + pad, size - pad * 2, size - pad * 2)

      const dataUrl = canvas.toDataURL('image/png')
      setCompositeUrl(dataUrl)
      setCompositeReady(true)

      // Also draw scaled version in preview canvas
      if (previewRef.current) {
        const preview = previewRef.current
        const scale = preview.offsetWidth / W
        preview.width = preview.offsetWidth
        preview.height = Math.round(H * scale)
        const pctx = preview.getContext('2d')!
        pctx.drawImage(canvas, 0, 0, preview.width, preview.height)
      }
    }

    build()
    return () => { cancelled = true }
  }, [tab, url])

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDownload = () => {
    if (!compositeUrl) return
    const a = document.createElement('a')
    a.href = compositeUrl
    a.download = `${shopName}-qr.png`
    a.click()
  }

  return (
    <div className="share-modal-backdrop" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>

        <div className="share-modal-header">
          <span className="share-modal-title">Share Shop</span>
          <button className="share-modal-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <p className="share-modal-shop-name">{shopName}</p>

        <div className="share-modal-tabs">
          <button className={`share-tab${tab === 'link' ? ' active' : ''}`} onClick={() => setTab('link')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
            Copy Link
          </button>
          <button className={`share-tab${tab === 'qr' ? ' active' : ''}`} onClick={() => setTab('qr')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/>
              <rect x="19" y="14" width="2" height="2"/><rect x="14" y="19" width="2" height="2"/>
              <rect x="18" y="18" width="3" height="3"/>
            </svg>
            QR Code
          </button>
        </div>

        {tab === 'link' && (
          <div className="share-link-panel">
            <div className="share-url-box">
              <span className="share-url-text">{url}</span>
            </div>
            <button className={`share-copy-btn${copied ? ' copied' : ''}`} onClick={handleCopy}>
              {copied ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  Copy Link
                </>
              )}
            </button>
          </div>
        )}

        {tab === 'qr' && (
          <div className="share-qr-panel">
            <div className="share-qr-preview-wrap">
              {!compositeReady && (
                <div className="share-qr-spinner-wrap">
                  <div className="share-qr-spinner" />
                  <span>Generating…</span>
                </div>
              )}
              <canvas
                ref={previewRef}
                className="share-qr-canvas"
                style={{ opacity: compositeReady ? 1 : 0 }}
              />
            </div>
            <button className="share-download-btn" onClick={handleDownload} disabled={!compositeReady}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download QR Image
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}
